import { useEffect, useMemo, useState } from 'react';
import { Toaster, toast } from 'react-hot-toast';
import {
  createCredential,
  decryptCredentialValue,
  listCredentials,
  updateCredentialField,
} from '../services';

const fieldConfig = [
  { key: 'name', label: 'Name' },
  { key: 'note', label: 'Note' },
  { key: 'host', label: 'Host' },
  { key: 'url', label: 'URL' },
  { key: 'username', label: 'Username' },
  { key: 'password', label: 'Password' },
  { key: 'secret', label: 'Secret' },
];

function getErrorMessage(error) {
  return error.response?.data?.error || error.response?.data?.message || error.message || 'Request failed';
}

function normalizeEmptyCredential() {
  return fieldConfig.reduce((accumulator, field) => {
    accumulator[field.key] = '';
    return accumulator;
  }, {});
}

function normalizeCredential(credential) {
  const nextValue = { id: credential?.id ?? null };

  fieldConfig.forEach((field) => {
    nextValue[field.key] = credential?.[field.key] ?? '';
  });

  return nextValue;
}

function buildDraftMap(items) {
  return items.reduce((accumulator, item) => {
    accumulator[item.id] = normalizeCredential(item);
    return accumulator;
  }, {});
}

function fieldContainsKeyword(value, keyword) {
  return String(value ?? '').toLowerCase().includes(keyword);
}

function DialogShell({ children, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/30 p-4">
      <button type="button" aria-label="Close dialog" className="absolute inset-0" onClick={onClose} />
      <div className="relative z-10 w-full max-w-md rounded-2xl border border-cyan-100 bg-white p-5 shadow-lg">
        {children}
      </div>
    </div>
  );
}

export default function CredentialsPage() {
  const [credentials, setCredentials] = useState([]);
  const [draftRows, setDraftRows] = useState({});
  const [createName, setCreateName] = useState('');
  const [manualDecryptValue, setManualDecryptValue] = useState('');
  const [manualDecryptPassword, setManualDecryptPassword] = useState('');
  const [searchKeyword, setSearchKeyword] = useState('');
  const [decryptedValues, setDecryptedValues] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [isManualDecrypting, setIsManualDecrypting] = useState(false);
  const [activeCellKey, setActiveCellKey] = useState('');
  const [dialogState, setDialogState] = useState(null);
  const [dialogPassword, setDialogPassword] = useState('');

  const filteredCredentials = useMemo(() => {
    const keyword = searchKeyword.trim().toLowerCase();

    if (!keyword) {
      return credentials;
    }

    return credentials.filter((credential) => {
      const draft = draftRows[credential.id] ?? normalizeCredential(credential);
      return fieldConfig.some((field) => fieldContainsKeyword(draft[field.key], keyword));
    });
  }, [credentials, draftRows, searchKeyword]);

  const dialogLabel = useMemo(() => {
    if (!dialogState?.field) {
      return '';
    }

    return fieldConfig.find((field) => field.key === dialogState.field)?.label || dialogState.field;
  }, [dialogState]);

  const hasBlockingDialog = Boolean(dialogState);

  useEffect(() => {
    if (!dialogState) {
      setDialogPassword('');
    }
  }, [dialogState]);

  useEffect(() => {
    loadCredentials();
  }, []);

  async function loadCredentials() {
    setIsLoading(true);

    try {
      const data = await listCredentials();
      setCredentials(data);
      setDraftRows(buildDraftMap(data));
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  }

  function handleCellChange(id, field, value) {
    setDraftRows((current) => ({
      ...current,
      [id]: {
        ...(current[id] ?? normalizeEmptyCredential()),
        id,
        [field]: value,
      },
    }));
  }

  function revertDraftValue(id, field, originalValue) {
    setDraftRows((current) => ({
      ...current,
      [id]: {
        ...(current[id] ?? normalizeEmptyCredential()),
        id,
        [field]: originalValue ?? '',
      },
    }));
  }

  function handleCellBlur(id, field) {
    if (hasBlockingDialog) {
      return;
    }

    const credential = credentials.find((item) => item.id === id);

    if (!credential) {
      return;
    }

    const draft = draftRows[id] ?? normalizeCredential(credential);
    const originalValue = credential[field] ?? '';
    const nextValue = draft[field] ?? '';

    if (originalValue === nextValue) {
      return;
    }

    setDialogState({
      mode: 'confirm-update',
      id,
      field,
      originalValue,
      nextValue,
      name: credential.name,
    });
  }

  function handleCellKeyDown(event) {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.currentTarget.blur();
    }
  }

  async function commitFieldUpdate({ id, field, nextValue, originalValue }, password = '') {
    const cellKey = `${id}-${field}`;
    setActiveCellKey(cellKey);

    try {
      const updatedCredential = await updateCredentialField(id, field, nextValue, password);
      setCredentials((current) =>
        current.map((credential) => (credential.id === updatedCredential.id ? updatedCredential : credential))
      );
      setDraftRows((current) => ({
        ...current,
        [id]: normalizeCredential(updatedCredential),
      }));
      setDecryptedValues((current) => {
        const next = { ...current };
        delete next[cellKey];
        return next;
      });
      toast.success(`Đã cập nhật ${dialogLabel || field}.`);
    } catch (error) {
      revertDraftValue(id, field, originalValue);
      toast.error(getErrorMessage(error));
    } finally {
      setActiveCellKey('');
      setDialogState(null);
    }
  }

  function handleCancelUpdate() {
    if (dialogState?.mode === 'confirm-update' || dialogState?.mode === 'encrypt-update') {
      revertDraftValue(dialogState.id, dialogState.field, dialogState.originalValue);
    }

    setDialogState(null);
  }

  function handleEncryptRequest() {
    setDialogState((current) =>
      current
        ? {
            ...current,
            mode: 'encrypt-update',
          }
        : current
    );
  }

  async function handleConfirmEncrypt() {
    if (!dialogPassword) {
      toast.error('Nhập mật khẩu để mã hóa trước khi lưu.');
      return;
    }

    await commitFieldUpdate(dialogState, dialogPassword);
  }

  async function handleConfirmDecrypt() {
    if (!dialogPassword) {
      toast.error('Nhập mật khẩu để giải mã.');
      return;
    }

    const cellKey = `${dialogState.id}-${dialogState.field}`;
    setActiveCellKey(cellKey);

    try {
      const decryptedValue = await decryptCredentialValue(dialogState.value, dialogPassword);
      setDecryptedValues((current) => ({
        ...current,
        [cellKey]: decryptedValue,
      }));
      toast.success(`Đã giải mã ${dialogLabel || dialogState.field}.`);
      setDialogState(null);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setActiveCellKey('');
    }
  }

  async function handleCreateCredential(event) {
    event?.preventDefault();

    const nextName = createName.trim();

    if (!nextName) {
      toast.error('Tên credential là bắt buộc.');
      return;
    }

    setIsCreating(true);

    try {
      const createdCredential = await createCredential(nextName);
      setCredentials((current) => [ ...current, createdCredential]);
      setDraftRows((current) => ({
        ...current,
        [createdCredential.id]: normalizeCredential(createdCredential),
      }));
      setCreateName('');
      toast.success('Đã tạo credential mới.');
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsCreating(false);
    }
  }

  async function handleManualDecrypt(event) {
    event.preventDefault();

    if (!manualDecryptValue.trim()) {
      toast.error('Vui lòng nhập giá trị cần giải mã.');
      return;
    }

    if (!manualDecryptPassword) {
      toast.error('Vui lòng nhập mật khẩu giải mã.');
      return;
    }

    setIsManualDecrypting(true);

    try {
      const plaintext = await decryptCredentialValue(manualDecryptValue, manualDecryptPassword);
      setDialogState({
        mode: 'plaintext-result',
        title: 'Nội dung plaintext',
        plaintext,
      });
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsManualDecrypting(false);
    }
  }

  async function handleCopyPlaintext() {
    if (!dialogState?.plaintext) {
      return;
    }

    try {
      await navigator.clipboard.writeText(dialogState.plaintext);
      toast.success('Đã copy vào clipboard.');
    } catch (error) {
      toast.error('Không thể copy vào clipboard.');
    }
  }

  return (
    <section className='space-y-2'>
      <Toaster position="top-right" toastOptions={{ duration: 2500 }} />

      <div className="rounded-xl border border-cyan-100 bg-white p-3">
        <form className="flex flex-col gap-3 sm:flex-row sm:items-center" onSubmit={handleCreateCredential}>
          <input
            value={createName}
            onChange={(event) => setCreateName(event.target.value)}
            placeholder="Tên credential mới"
            className="min-w-0 flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none placeholder:text-slate-400 focus:border-cyan-400"
          />
          <button
            type="submit"
            disabled={isCreating}
            className="rounded-lg bg-cyan-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-60"
          >
            {isCreating ? 'Đang tạo...' : 'Tạo mới'}
          </button>
          <button
            type="button"
            onClick={loadCredentials}
            className="rounded-lg border border-cyan-200 px-4 py-2 text-sm text-cyan-700"
          >
            Làm mới
          </button>
        </form>
      </div>

      <div className="rounded-xl border border-cyan-100 bg-white p-3">
        <form className="grid gap-2 md:grid-cols-[minmax(0,1fr)_220px_120px]" onSubmit={handleManualDecrypt}>
          <input
            value={manualDecryptValue}
            onChange={(event) => setManualDecryptValue(event.target.value)}
            placeholder="Giá trị cần giải mã"
            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none placeholder:text-slate-400 focus:border-cyan-400"
          />
          <input
            value={manualDecryptPassword}
            onChange={(event) => setManualDecryptPassword(event.target.value)}
            placeholder="Mật khẩu giải mã"
            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none placeholder:text-slate-400 focus:border-cyan-400"
          />
          <button
            type="submit"
            disabled={isManualDecrypting}
            className="rounded-lg bg-cyan-600 px-3 py-2 text-sm font-medium text-white disabled:opacity-60"
          >
            {isManualDecrypting ? 'Đang giải mã...' : 'Giải mã'}
          </button>
        </form>
      </div>

      <div className="rounded-xl border border-cyan-100 bg-white p-3">
        <input
          value={searchKeyword}
          onChange={(event) => setSearchKeyword(event.target.value)}
          placeholder="Tìm trong tất cả trường"
          className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none placeholder:text-slate-400 focus:border-cyan-400"
        />
      </div>

      <div className="overflow-hidden rounded-2xl border border-cyan-100 bg-white">
        <div className="border-b border-cyan-100 px-4 py-3">
          <h3 className="text-lg font-semibold text-slate-900">Bảng credentials</h3>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-[1100px] table-fixed border-collapse">
            <thead>
              <tr className="bg-slate-50 text-left text-xs uppercase tracking-[0.14em] text-slate-500">
                <th className="w-20 border-b border-slate-200 px-2 py-2 font-medium">ID</th>
                {fieldConfig.map((field) => (
                  <th key={field.key} className="border-b border-slate-200 px-2 py-2 font-medium">
                    {field.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={fieldConfig.length + 1} className="px-4 py-6 text-center text-sm text-slate-500">
                    Đang tải credentials...
                  </td>
                </tr>
              ) : filteredCredentials.length ? (
                filteredCredentials.map((credential) => {
                  const draft = draftRows[credential.id] ?? normalizeCredential(credential);

                  return (
                    <tr key={credential.id} className="align-top odd:bg-white even:bg-slate-50/50">
                      <td className="border-b border-slate-100 px-2 py-2 text-sm text-slate-500">#{credential.id}</td>
                      {fieldConfig.map((field) => {
                        const cellKey = `${credential.id}-${field.key}`;
                        const isBusy = activeCellKey === cellKey;
                        const decryptedValue = decryptedValues[cellKey];

                        return (
                          <td key={field.key} className="border-b border-slate-100 px-2 py-2">
                            <div className="space-y-1">
                              <input
                                value={draft[field.key] ?? ''}
                                onChange={(event) => handleCellChange(credential.id, field.key, event.target.value)}
                                onBlur={() => handleCellBlur(credential.id, field.key)}
                                onKeyDown={handleCellKeyDown}
                                placeholder={field.key}
                                className="w-full rounded-md border border-slate-200 bg-white px-2 py-1.5 text-sm outline-none focus:border-cyan-400"
                              />

                              <div className="flex min-h-4 items-center justify-end gap-2 text-xs">
                                {isBusy && <span className="text-slate-400">Đang lưu...</span>}
                              </div>

                              {decryptedValue && (
                                <div className="rounded-md bg-cyan-50 px-2 py-1.5 text-xs text-slate-700">
                                  {decryptedValue}
                                </div>
                              )}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={fieldConfig.length + 1} className="px-4 py-6 text-center text-sm text-slate-500">
                    Không có credential nào phù hợp.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {dialogState?.mode === 'confirm-update' && (
        <DialogShell onClose={handleCancelUpdate}>
          <h4 className="text-lg font-semibold text-slate-900">Mã hóa trước khi cập nhật?</h4>
          <p className="mt-2 text-sm leading-6 text-slate-600">
            Trường {dialogLabel} của credential {dialogState.name} đã thay đổi. Bạn muốn gửi lên backend với mã hóa hay lưu thẳng?
          </p>
          <div className="mt-5 flex justify-end gap-2">
            <button
              type="button"
              onClick={handleCancelUpdate}
              className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-700"
            >
              Hủy
            </button>
            <button
              type="button"
              onClick={() => commitFieldUpdate(dialogState, '')}
              className="rounded-lg border border-cyan-200 px-4 py-2 text-sm text-cyan-700"
            >
              Lưu thường
            </button>
            <button
              type="button"
              onClick={handleEncryptRequest}
              className="rounded-lg bg-cyan-600 px-4 py-2 text-sm font-medium text-white"
            >
              Mã hóa
            </button>
          </div>
        </DialogShell>
      )}

      {dialogState?.mode === 'encrypt-update' && (
        <DialogShell onClose={handleCancelUpdate}>
          <h4 className="text-lg font-semibold text-slate-900">Nhập mật khẩu để mã hóa</h4>
          <p className="mt-2 text-sm leading-6 text-slate-600">
            Mật khẩu này sẽ được gửi kèm khi cập nhật trường {dialogLabel}.
          </p>
          <input
            value={dialogPassword}
            onChange={(event) => setDialogPassword(event.target.value)}
            placeholder="Mật khẩu mã hóa"
            className="mt-4 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-cyan-400"
          />
          <div className="mt-5 flex justify-end gap-2">
            <button
              type="button"
              onClick={handleCancelUpdate}
              className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-700"
            >
              Hủy
            </button>
            <button
              type="button"
              onClick={handleConfirmEncrypt}
              className="rounded-lg bg-cyan-600 px-4 py-2 text-sm font-medium text-white"
            >
              Xác nhận mã hóa
            </button>
          </div>
        </DialogShell>
      )}

      {dialogState?.mode === 'decrypt-value' && (
        <DialogShell onClose={() => setDialogState(null)}>
          <h4 className="text-lg font-semibold text-slate-900">Giải mã {dialogLabel}</h4>
          <p className="mt-2 text-sm leading-6 text-slate-600">
            Nhập mật khẩu để gọi API giải mã cho trường này.
          </p>
          <input
            value={dialogPassword}
            onChange={(event) => setDialogPassword(event.target.value)}
            placeholder="Mật khẩu để giải mã"
            className="mt-4 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-cyan-400"
          />
          <div className="mt-5 flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setDialogState(null)}
              className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-700"
            >
              Hủy
            </button>
            <button
              type="button"
              onClick={handleConfirmDecrypt}
              className="rounded-lg bg-cyan-600 px-4 py-2 text-sm font-medium text-white"
            >
              Giải mã
            </button>
          </div>
        </DialogShell>
      )}

      {dialogState?.mode === 'plaintext-result' && (
        <DialogShell onClose={() => setDialogState(null)}>
          <h4 className="text-lg font-semibold text-slate-900">{dialogState.title}</h4>
          <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800 break-all">
            {dialogState.plaintext}
          </div>
          <div className="mt-5 flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setDialogState(null)}
              className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-700"
            >
              Đóng
            </button>
            <button
              type="button"
              onClick={handleCopyPlaintext}
              className="rounded-lg bg-cyan-600 px-4 py-2 text-sm font-medium text-white"
            >
              Sao chép
            </button>
          </div>
        </DialogShell>
      )}
    </section>
  );
}
